import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { ReplaySubject } from "@ciaopeople/cp-shatsu/Observable";
import { DOMUtils } from "@mp-commons/lib/utils/DOMUtils.js";
import { CPNetworkName } from "@mp-commons/config/CPNetworks.js";
import { AdContainerControls } from "@mp-commons/modules/ads/AdsController.js";
import { MediaControlOrigin } from "@mp-commons/modules/events/PlayerEvent.js";
import { MediaStatus } from "@mp-commons/modules/player/AbstractPlayerControls.js";
import { GenericFormatLabel, ParsedFormat } from "@mp-commons/modules/player/video/FormatAndSizePreference.js";
import { VideoPlayerElements, VideoContext } from "@mp-commons/modules/player/Player.js";
import { VideoContextOverrider } from "@mp-commons/modules/player/video/VideoContextOverrider.js";
import { MegaridePlayerGUI } from "../../../../MegaridePlayer.js";
import { IconName, getAudioIconName, getIconSVG, getPlayIconName } from "../../../../gui/IconSVG.js";
import { RoundIcon, RoundIconButton, RoundIconTheme } from "../../../../gui/RoundIcon.js";
import { VideoItem } from "../../../../gui/VideoItem.js";
import { PARTHENOPE_GLOBAL_CSS_PREFIX } from "../../ParthenopeGUIV2.js";
import { ControlBar } from "./ControlBar.js";
import { ControlCast } from "../../../../gui/ControlCast.js";
import { ControlSelectModal, selectItemForSettings, selectItemsForSettings } from "./ControlSelect.js";
import { Modal } from "./Modal.js";
import { getPosterUrl, PosterFormat } from "@mp-commons/model/MediaThumbnailResource.js";

export interface MainComponentProps {
    titleBar: boolean,
    titlePlaceholder?: string,
    initialVolume?: number,
    initialPoster: string,
    onlyVideoPoster: boolean,
    enableNavigationPlaylist?: boolean,
    enableCast?: boolean,
    product: CPNetworkName,
    enableStoryboard: boolean
}


class InternalWrappedContext extends VideoContextOverrider {

    constructor(cl: VideoContext, private beforePlay: () => void, private onPlay: (playStatus: boolean, manualPlay: boolean) => void, private onSwitchQuality: (playing: boolean) => void) {
        super(cl);
    }

    public switchQuality(format: GenericFormatLabel): Promise<any> {
        this.onSwitchQuality(false);
        return this.originalContext.switchQuality(format).then(_ => this.onSwitchQuality(true));
    }

    public togglePlay(manualPlay: boolean): Promise<boolean> {
        this.beforePlay();
        return this.originalContext.togglePlay(manualPlay).then(playing => {
            this.onPlay(playing, manualPlay);
            return playing;
        });
    }

}

export class MainComponent {

    private readonly bClass = `${PARTHENOPE_GLOBAL_CSS_PREFIX}`;
    private readonly upperLimitBig = 640;
    private readonly upperLimitHeadBig = 537;
    private readonly vertical_class = "vertical"
    private readonly horizontal_class = "horizontal"

    private mainHtml: HTMLElement;
    private videoContainer: HTMLElement;
    private middle: HTMLElement;
    private videoEl: HTMLVideoElement;
    private head: HTMLElement;
    private titlePlaceholders: HTMLCollection;
    private poster: HTMLElement;
    private previewThumb: HTMLElement;
    private controlBar: ControlBar;
    private audioIconButton: RoundIconButton;
    private prevIconButton: RoundIconButton;
    private nextIconButton: RoundIconButton;
    private playIconButton: RoundIconButton;
    private settings: ControlSelectModal<ParsedFormat>;
    private subtitle: ControlSelectModal<string>;

    private castButton: ControlCast;
    private castHtml: HTMLElement;
    private modal: Modal;

    private adContainer: HTMLElement;
    private adControlBar: ControlBar;
    private adCloseButton: HTMLElement;

    private loader: HTMLElement;
    private customClickLayer: HTMLElement;

    private videoContext: VideoContext;
    private adContainerCtls: AdContainerControls;
    private wvDetect: DetectedWebView;
    private idleTimeout = null;
    private fullscreen: ReplaySubject<boolean> = new ReplaySubject<boolean>(1, false);
    private doUpdatePoster: boolean = false;

    private evtListener: {
        stalled: EventListener,
        waiting: EventListener,
        timeupdate: EventListener,
        volumeChange: EventListener,
        ended: EventListener,
        play: EventListener,
        pause: EventListener,
        loadedMetadata: EventListener
    }
    private get isDesktop(): boolean {
        return this.wvDetect.device?.other
    }

    private get isMobile(): boolean {
        return this.wvDetect.device?.phone || this.wvDetect.native_app
    }

    get headEl(): HTMLElement {
        return this.head;
    }

    private currentPlayingStatus: boolean = false;

    constructor(private readonly gui: MegaridePlayerGUI,
        private readonly props: MainComponentProps,
        maybeVideoElement?: HTMLVideoElement,
    ) {
        this.wvDetect = detectWebView();

        this.evtListener = {
            stalled: null,
            waiting: null,
            volumeChange: null,
            timeupdate: null,
            ended: null,
            play: null,
            pause: null,
            loadedMetadata: null
        }

        this.head = null;
        this.wvDetect = detectWebView();
        this.videoEl = maybeVideoElement;
        this.videoContainer = null;
        this.videoContext = null;
        this.adContainer = null;
        this.customClickLayer = null;
        this.adContainerCtls = null;
        this.controlBar = null
        this.adControlBar = null
        this.previewThumb = null;
        this.prevIconButton = null;
        this.nextIconButton = null;
        this.playIconButton = null;
        this.settings = null;
        this.subtitle = null;

        this.initMainHtml();
    }

    public fullscreenChanged(fullscreen: boolean): Promise<boolean> {
        this.fullscreen.next(fullscreen);
        this.mainHtml.classList.toggle(`${this.bClass}--full-screen`);
        this.controlBar.fullscreenChanged(fullscreen);
        return Promise.resolve(fullscreen)
    }

    public get html(): HTMLElement {
        return this.mainHtml;
    }

    private get playerControls() {

        this.audioIconButton = new RoundIconButton({ name: IconName.audio_off, theme: RoundIconTheme.brand }, PARTHENOPE_GLOBAL_CSS_PREFIX);
        this.audioIconButton.onClick((evt) => {
            this.videoContext.toggleAudio();
        })

        if (this.isMobile) {
            this.modal = new Modal();
            this.playIconButton = new RoundIconButton({ name: getPlayIconName(this.videoContext?.isPaused || true), theme: RoundIconTheme.dark }, PARTHENOPE_GLOBAL_CSS_PREFIX);
            this.playIconButton.onClick((evt) => {
                evt.stopPropagation();
                evt.stopImmediatePropagation();
                evt.preventDefault();
                if (this.gui.restartEnabled) {
                    this.gui.restart(true);
                } else {
                    this.videoContext.togglePlay(true);
                }
            });

            this.settings = new ControlSelectModal<ParsedFormat>(IconName.settings, [], this.modal, 'Qualità Video');
            this.settings.onChange((switchTo: ParsedFormat) => {
                this.videoContext.switchQuality(switchTo.label);
            })


            // Cast button is opt-in via props.enableCast (only when the underlying factory is castable).
            // Same platform gate as Leucosia: phone / native app + chrome|safari.
            if (this.props.enableCast
                && (this.wvDetect.device?.phone || this.wvDetect?.native_app)
                && (this.wvDetect.browser.chrome || this.wvDetect.browser.safari)) {
                this.castButton = new ControlCast(this.wvDetect, PARTHENOPE_GLOBAL_CSS_PREFIX);
                this.castButton.onClick((evt) => {
                    this.gui.loadingUpdate(true);
                    this.gui.toggleCast(evt);
                });
            }

            if (this.props.enableNavigationPlaylist) {
                this.nextIconButton = new RoundIconButton({ name: IconName.next, theme: RoundIconTheme.dark }, PARTHENOPE_GLOBAL_CSS_PREFIX);
                this.nextIconButton.onClick((evt) => {
                    evt.stopPropagation();
                    evt.stopImmediatePropagation();
                    evt.preventDefault();
                    this.gui.playNextVideo(true);
                });

                this.prevIconButton = new RoundIconButton({ name: IconName.prev, theme: RoundIconTheme.dark }, PARTHENOPE_GLOBAL_CSS_PREFIX);
                this.prevIconButton.onClick((evt) => {
                    evt.stopPropagation();
                    evt.stopImmediatePropagation();
                    evt.preventDefault();
                    this.gui.playPreviousVideo(true);
                });
            }

            return <>
                {this.audioIconButton.html}
                {this.prevIconButton?.html}
                {this.nextIconButton?.html}
                {this.playIconButton?.html}
                <div class={`${this.bClass}__mobile-menu`}>
                    {this.castButton?.html}
                    {this.subtitle?.html}
                    {this.settings?.html}
                </div>
            </>
        } else {
            return <>
                {this.audioIconButton.html}
                {new RoundIcon({ name: IconName.play, theme: RoundIconTheme.dark }, PARTHENOPE_GLOBAL_CSS_PREFIX).html}
                {new RoundIcon({ name: IconName.pause, theme: RoundIconTheme.dark }, PARTHENOPE_GLOBAL_CSS_PREFIX).html}
            </>
        }

    }

    public getPlayerVideoElement(): VideoPlayerElements {
        return {
            mainItem: this.videoContainer,
            mediaWrapper: this.videoContainer,
            mediaItem: this.videoEl
        }
    }

    public getAdContainer(): AdContainerControls {

        if (!this.adContainerCtls) {
            const self = this;
            this.adContainerCtls = {
                container: this.adContainer,
                customClickLayer: this.customClickLayer,
                show: (adInfo: google.ima.Ad) => {
                    this.adContainer.classList.remove("disabled");
                    //refreshEl(this.adContainer);
                    if (adInfo && !adInfo.isLinear()) {
                        this.adContainer.classList.remove("linear-adv");
                        this.controlBar.html.classList.add("ymp-bar--s")
                    } else {
                        this.adContainer.classList.add("linear-adv");
                    }
                },
                hide: () => {
                    this.adContainer.classList.add("disabled")
                    this.adContainer.classList.remove("linear-adv");
                    this.controlBar.html.classList.remove("ymp-bar--s")

                    if (this.customClickLayer) this.customClickLayer.classList.add("disabled")
                    if (this.adCloseButton) this.adCloseButton.classList.add("disabled");
                },
                refresh: () => {
                    //refreshEl(this.adContainer);
                },
                signalPlayStatus: (newStatus: boolean) => {
                    this.handleTogglePlayIcons(newStatus, true)
                },
                signalAudioStatus: (newStatus: boolean) => {
                    this.updateAudioIconStatus(newStatus)
                },
                signalAdProgress: (time: number, duration: number) => {
                    this.adControlBar.signalAdProgress(time, duration)
                },
                showCustomClickLayer: () => {
                    if (this.customClickLayer) this.customClickLayer.classList.remove("disabled")
                },
                hideCustomClickLayer: () => {
                    if (this.customClickLayer) this.customClickLayer.classList.add("disabled")
                },
                onNextCloseClick: (listener: () => void) => {
                    const ll = (evt: Event) => {
                        listener();
                        this.adCloseButton.removeEventListener("click", ll);
                    }
                    this.adCloseButton.addEventListener("click", ll);
                },
                showCloseControl: () => {
                    this.adCloseButton.classList.remove("disabled");
                },
                hideCloseControl: () => {
                    this.adCloseButton.classList.add("disabled");
                },
                get fullscreen() {
                    return self.fullscreen.getCurrentValue();
                },
                signalAdWaiting(isWaiting: boolean) {
                    self.loadingUpdate(isWaiting && self.videoContext.isPaused);
                }

            }
        }
        return this.adContainerCtls;
    }

    private isSteady() {
        return this.mainHtml.classList.contains("ymp--steady");
    }
    private setSteady(steady: boolean) {
        if (steady) {
            this.mainHtml.classList.add("ymp--steady");
        } else {
            this.mainHtml.classList.remove("ymp--steady");
        }
    }

    private handleTogglePlayIcons(playing: boolean, manualPlay: boolean) {

        if (manualPlay) {
            if (this.isMobile) {
                if (this.isSteady()) {
                    this.setSteady(false);
                }
                this.playIconButton.changeIcon(getPlayIconName(!playing));
            } else {

                if (this.isSteady()) {
                    this.setSteady(false);
                } else {
                    if (playing != this.currentPlayingStatus) {
                        if (playing) {
                            this.mainHtml.classList.add(`${this.bClass}--run`)
                            this.mainHtml.classList.remove(`${this.bClass}--pause`)
                        } else {
                            this.mainHtml.classList.add(`${this.bClass}--pause`)
                            this.mainHtml.classList.remove(`${this.bClass}--run`)
                        }
                    }
                }
            }
        }
        this.currentPlayingStatus = playing;
    }


    private buildMainVideoContext(ctx: VideoContext): VideoContext {
        return new InternalWrappedContext(ctx,
            () => this.loadingUpdate(true),
            (ps: boolean, manualPlay: boolean) => {
                this.handleTogglePlayIcons(ps, manualPlay)
                this.loadingUpdate(false);
            },
            (playing: boolean) => this.loadingUpdate(!playing));
    }

    public setVideoContext(ctx: VideoContext) {

        const mainCtx = this.buildMainVideoContext(ctx)
        this.controlBar?.setVideoContext(mainCtx);

        let ectime = 0;
        let stall_start = -1;

        const ld = (e) => {
            if (mainCtx.currentTime - ectime > 0.3) {
                stall_start = -1;
                console.log("Hiding loader");
                this.loadingUpdate(false)
                mainCtx.off("timeupdate", ld);
            }
        }

        const sd = (e) => {
            if (stall_start < 0) {
                stall_start = new Date().getTime();
            }

            else if (new Date().getTime() - stall_start > 500) {
                this.loadingUpdate(true)
                ectime = mainCtx.currentTime;
                mainCtx.on("timeupdate", ld);
            }
        }

        mainCtx.on("stalled", sd);
        mainCtx.on("waiting", sd);

        const volumeChangeListener = (evt) => {
            this.videoContext.muted().then(muted => {
                this.updateAudioIconStatus(muted);
            })
        }

        const onceTimeupdate = (evt) => {
            this.setSteady(false);
            this.setPreviewThumbSize();
            this.videoContext.muted().then(muted => {
                this.updateAudioIconStatus(muted);
            })
            if (mainCtx?.currentTime > 0.1) {
                this.removePosterImg();
                this.loadingUpdate(false)
                mainCtx.off("timeupdate", onceTimeupdate);
            }
        }

        mainCtx.on("volumechange", volumeChangeListener);
        mainCtx.on("timeupdate", onceTimeupdate);

        if (this.evtListener?.stalled) this.videoContext.off("stalled", this.evtListener.stalled);
        this.evtListener.stalled = sd;

        if (this.evtListener?.waiting) this.videoContext.off("waiting", this.evtListener.waiting);
        this.evtListener.waiting = sd;

        if (this.evtListener?.volumeChange) this.videoContext.off("volumechange", this.evtListener.volumeChange);
        this.evtListener.volumeChange = volumeChangeListener;

        if (this.evtListener?.timeupdate) this.videoContext.off("timeupdate", this.evtListener.timeupdate);
        this.evtListener.timeupdate = onceTimeupdate;

        if (this.playIconButton) {
            this.playIconButton.changeIcon(getPlayIconName(ctx.isPaused || false));

            const playListener = (evt) => {
                this.playIconButton.changeIcon(getPlayIconName(false));
            };
            const pauseListener = (evt) => {
                this.playIconButton.changeIcon(getPlayIconName(true));
            };

            const endedListener = (evt) => {
                if (!this.gui.forwardNavigationEnabled) {
                    this.playIconButton.changeIcon(IconName.restart);
                }
            }

            mainCtx.on("ended", endedListener);

            mainCtx.on("play", playListener);
            mainCtx.on("pause", pauseListener);


            if (this.evtListener?.play) this.videoContext.off("play", this.evtListener.play);
            this.evtListener.play = playListener;

            if (this.evtListener?.pause) this.videoContext.off("pause", this.evtListener.pause);
            this.evtListener.pause = pauseListener;

            if (this.evtListener?.ended) this.videoContext.off("ended", this.evtListener.ended);
            this.evtListener.ended = endedListener;
        }

        if (this.settings) {

            const loadedMetadataListener = () => {
                if (this.settings) {
                    this.settings.updateSelectedItem(selectItemForSettings(this.videoContext.getCurrentFormat(), true))
                }
                this.videoContext.once("loadedmetadata", loadedMetadataListener)
            }


            mainCtx.metadataPromise.then(_ => {

                if (this.settings) {
                    this.settings.updateItems(
                        selectItemsForSettings(
                            mainCtx.getAvailableQualities(),
                            mainCtx.getCurrentFormat()
                        )
                    );
                    mainCtx.once("loadedmetadata", loadedMetadataListener)
                }
            })

            if (this.evtListener?.loadedMetadata) this.videoContext.off("loadedmetadata", this.evtListener.loadedMetadata);
            this.evtListener.loadedMetadata = loadedMetadataListener;

        }

        if (this.controlBar) {
            this.controlBar.setVideoContext(mainCtx);
        }

        this.videoContext = mainCtx;

        this.videoContext.muted().then(muted => {
            this.updateAudioIconStatus(muted);
        })

        if (this.head) {
            this.head.firstElementChild.innerHTML = this.videoContext?.ref?.info?.title;
        }

        if (this.titlePlaceholders) {

            const title = this.videoContext?.ref?.info?.title;
            const videoUrl = this.videoContext.ref.info.url;
            const titleAnchor = window.document.createElement('a');
            titleAnchor.href = videoUrl;
            titleAnchor.innerText = title;

            for (let index = 0; index < this.titlePlaceholders.length; index++) {
                this.titlePlaceholders.item(index).innerHTML = titleAnchor.outerHTML;
            }

        }

        if (this.prevIconButton) {
            this.prevIconButton.updateStatus(this.gui.backNavigationEnabled);
        }

        if (this.nextIconButton) {
            this.nextIconButton.updateStatus(this.gui.forwardNavigationEnabled);
        }

        const isHorizontal = this.videoContext?.getCurrentFormat()?.parsedSize?.aspectRatio.isHorizontal() || false;

        if (isHorizontal) {
            this.videoEl.classList.remove(this.vertical_class)
            this.videoEl.classList.add(this.horizontal_class)
        } else {
            this.videoEl.classList.remove(this.horizontal_class)
            this.videoEl.classList.add(this.vertical_class)
        }

        if (this.doUpdatePoster) {
            this.updatePoster(this.videoContext);
        } else {
            if (this.poster) {
                const useTitle = this.videoContext?.ref?.info?.title ? ctx?.ref?.info?.title : "VideoPoster";
                this.poster.setAttribute("alt", useTitle);
            }
            this.doUpdatePoster = true;
        }

    }

    private updateAudioIconStatus(muted: boolean) {
        if (muted) {
            this.mainHtml.classList.add(`${this.bClass}--mute`);
        } else {
            this.mainHtml.classList.remove(`${this.bClass}--mute`);
        }

        this.audioIconButton.changeIcon(getAudioIconName(muted));
    }

    private setPreviewThumbSize() {
        const videoRatio = this.videoEl.videoWidth / this.videoEl.videoHeight;
        const clientVideoRatio = this.videoEl.clientWidth / this.videoEl.clientHeight;

        const c = videoRatio > clientVideoRatio

        const width = c ? this.videoEl.clientWidth : this.videoEl.clientHeight * videoRatio
        const height = c ? this.videoEl.clientWidth / videoRatio : this.videoEl.clientHeight

        this.previewThumb.style.width = `${width}px`;
        this.previewThumb.style.height = `${height}px`;
    }


    private removePosterImg(): void {
        if (this.poster) {
            this.poster.style.display = "none";
        }

    }


    private updatePoster(ctx: VideoContext): void {
        this.removePosterImg();
        const useTitle = ctx?.ref?.info?.title ? ctx?.ref?.info?.title : "VideoPoster";
        const posterResource = ctx?.ref?.info?.thumbnail;
        if (posterResource) {
            const posterUrl = getPosterUrl(posterResource, this.wvDetect, this.props.onlyVideoPoster ? PosterFormat.SMALL : undefined);
            if (this.poster && !this.props.onlyVideoPoster) {
                this.poster.setAttribute("src", posterUrl);
                this.poster.setAttribute("alt", useTitle);
                this.poster.style.display = "block";
            }
            this.videoEl.setAttribute("poster", posterUrl)
        }
    }



    private initMainHtml(): void {
        const nuel = <VideoItem autoplay={false} ></VideoItem>;
        const closeAdIcon = getIconSVG(IconName.closeAd);

        if (this.videoEl) {
            this.videoEl = DOMUtils.removeAllAttributes(this.videoEl) as HTMLVideoElement;
            this.videoEl = DOMUtils.copyAttributes(nuel, this.videoEl) as HTMLVideoElement;
            nuel.remove();
        }
        else {
            this.videoEl = nuel;
        }

        this.adControlBar = new ControlBar(this.gui, this.videoContext, {
            static: true,
            isAdv: true,
            progressBar: { enableSeek: false, enableStoryboard: false },
            controls: {
                enable: false,
                enablePlaylistNavigation: false,
            }
        });

        this.adCloseButton = <div class={`${this.bClass}__adv-btn`}>{closeAdIcon}</div>
        this.adContainer = <div class={`${this.bClass}__adv disabled`}>
            {this.adCloseButton}
            {this.adControlBar.html}
        </div>

        this.customClickLayer = <div class={`${this.bClass}__cc disabled`}></div>

        this.loader = <div class="ymp-loader"></div>

        this.castHtml = <div class={`${this.bClass}-cast`}>
            <div class={`${this.bClass}-cast__box`}>
                {getIconSVG(IconName.cast_on_big)}
                <div class={`${this.bClass}-cast__box-ttl`}>Trasmissione in corso</div>
                <div class={`${this.bClass}-cast__box-txt`}>Questo video è in riproduzione sul dispositivo remoto</div>
            </div>
        </div>;



        this.controlBar = new ControlBar(this.gui,
            this.videoContext,
            {
                static: false,
                isAdv: false,
                progressBar: {
                    enableSeek: true,
                    enableStoryboard: this.props.enableStoryboard
                },
                controls: {
                    enable: true,
                    enablePlaylistNavigation: (this.props.enableNavigationPlaylist && !this.isMobile) || false
                }
            });


        if (!this.poster) {
            const useTitle = this.videoContext?.ref?.info?.title ? this.videoContext?.ref?.info?.title : "VideoPoster";

            if (!this.props.onlyVideoPoster) this.poster = <img class={`${this.bClass}__poster`} src={this.props.initialPoster} alt={useTitle}></img>
            this.videoEl.setAttribute("poster", this.props.initialPoster)
        }

        this.head = this.props.titleBar ? <div class={`${this.bClass}__head`}>
            <div class={`${this.bClass}__head-inner`}>{this.videoContext?.ref?.info?.title}</div></div> : null;

        this.titlePlaceholders = this.props.titlePlaceholder ? this.gui.window.document.getElementsByClassName(this.props.titlePlaceholder) : null;

        this.mainHtml = <div class={`${this.bClass} ${this.bClass}--steady ${this.gui.generalMute ? `${this.bClass}--mute` : ``} ${this.props.titleBar ? `${this.bClass}--bar` : ``} ${this.bClass}--${this.props.product}`}>
            {this.head}
            <div class={`${this.bClass}__middle`} ref={(el: HTMLElement) => this.middle = el}>
                {this.adContainer}
                {this.playerControls}
                <div class={`${this.bClass}__content`} ref={(el: HTMLElement) => this.videoContainer = el}>
                    {this.poster}
                    {this.videoEl}
                    {this.loader}
                    <div class={`${this.bClass}__preview-layer`}>
                        <div class={`${this.bClass}__preview`} ref={(el: HTMLElement) => this.previewThumb = el}></div>
                    </div>
                    {this.castHtml}
                </div>
                {this.controlBar.html}
                {this.customClickLayer}
            </div>
        </div>;

        // iOS AirPlay won't engage on a video whose playback origin is still
        // autoplay (programmatic). Lock the cast button until the user has
        // done at least one intentional play — tapping it earlier wouldn't
        // be able to bind the AirPlay session anyway.
        //
        // We unlock on the first `play` event whose firing happens inside an
        // active transient activation window (navigator.userActivation.isActive
        // === true). That distinguishes user-initiated play from autoplay and
        // — critically — from a manual *pause*, which doesn't count: pausing
        // doesn't promote the playback origin, only playing does.
        if (this.castButton && this.wvDetect.platform?.ios) {
            this.mainHtml.classList.add(`${this.bClass}--cast-locked`);
            const onUserPlay = () => {
                const ua = (this.gui.window.navigator as any).userActivation;
                if (ua?.isActive) {
                    this.mainHtml.classList.remove(`${this.bClass}--cast-locked`);
                    this.videoEl.removeEventListener("play", onUserPlay);
                }
            };
            this.videoEl.addEventListener("play", onUserPlay);
        }

        this.modal?.updateContainer(this.middle);

        this.togglePlayerDimensionsClasses();

        this.attachClientEventListener()
    }

    private togglePlayerDimensionsClasses() {
        if (!this.isMobile && this.mainHtml.clientWidth > this.upperLimitBig) {
            this.mainHtml.classList.add(`${this.bClass}--b`)
        } else {
            this.mainHtml.classList.remove(`${this.bClass}--b`)
        }

        if (this.controlBar && !this.isMobile) {
            const show = this.mainHtml.clientHeight > 168 && this.mainHtml.clientWidth > 300;
            this.controlBar.toggleSettings(show);
        } else if (this.settings) {
            const show = this.mainHtml.clientHeight > 177 && this.mainHtml.clientWidth > 313;
            this.settings.toggle(show);
        }

        // il resize dell'head deve avvenire solo se c'è la classe ymp--bar sul player
        if (this.mainHtml.classList.contains(`${this.bClass}--bar`)) {
            if (!this.isMobile && this.mainHtml.clientWidth >= this.upperLimitHeadBig) {
                this.head?.classList.add(`${this.bClass}__head--big`)
            } else {
                this.head?.classList.remove(`${this.bClass}__head--big`)
            }
        }


    }

    anchorageUpdate(anchored: boolean) {
        this.toggleHead(!anchored)
    }

    loadingUpdate(loading: boolean): void {
        if (loading) {
            this.mainHtml.classList.add(`${this.bClass}--loading`);
        } else {
            this.mainHtml.classList.remove(`${this.bClass}--loading`);
        }
    }

    updateCastStatus(enabled: boolean): void {
        this.castButton?.updateStatus(enabled);

        if (enabled) {
            this.mainHtml.classList.add(`${this.bClass}--cast`);
            // AirPlay can't switch source mid-session without breaking the transition;
            // hide the quality picker so the user can't trigger it while cast is active.
            const isAirPlayPlatform = this.wvDetect?.platform?.ios || this.wvDetect?.platform?.macosx;
            if (enabled && isAirPlayPlatform) {
                this.mainHtml.classList.add(`${this.bClass}--airplay-cast`);
            }
        } else {
            this.mainHtml.classList.remove(`${this.bClass}--cast`);
            this.mainHtml.classList.remove(`${this.bClass}--airplay-cast`);
       }

        this.loadingUpdate(false);
    }

    private toggleHead(show: boolean) {
        if (this.head)
            if (!show) {
                this.head.classList.add(`${this.bClass}__head--hidden`)
                this.mainHtml.classList.remove(`${this.bClass}--bar`)
            } else {
                this.mainHtml.classList.add(`${this.bClass}--bar`)
                this.head.classList.remove(`${this.bClass}__head--hidden`)
            }
    }

    private attachClientEventListener(): void {

        const handleIdleTimeout = (handler: (evt: any) => void) => {
            if (this.idleTimeout) {
                this.gui.window.clearTimeout(this.idleTimeout);
                this.idleTimeout = null;
            }
            if (handler)
                this.idleTimeout = this.gui.window.setTimeout(handler, 2500);
        }

        this.gui.internalEvents.on("player_resize", (evt) => {
            this.setPreviewThumbSize();
            this.togglePlayerDimensionsClasses();
        })

        this.videoContainer.addEventListener('click', (evt) => {
            if (this.isDesktop || this.mainHtml.classList.contains(`${this.bClass}--focus`)) {

                if (this.gui.restartEnabled) {
                    this.gui.restart(true);
                } else {
                    this.videoContext.togglePlay(true);
                }
            }

        })

        const unfocusHandler = () => {
            const doUnfocus = !this.controlBar.active && (!this.isMobile || (this.isMobile && (this.gui.mediaState.status != MediaStatus.PAUSED || this.gui.mediaState.origin == MediaControlOrigin.ADV)));
            if (doUnfocus) {
                this.mainHtml.classList.remove(`${this.bClass}--focus`);
                this.controlBar.closeControls();
                handleIdleTimeout(null);
            } else {
                handleIdleTimeout(unfocusHandler);
            }
        }

        const focusToggler = (unfocusHandlerEnable: boolean) => {
            if (!this.modal?.isOpen) {
                this.mainHtml.classList.add(`${this.bClass}--focus`);
                if (unfocusHandlerEnable) {
                    handleIdleTimeout(unfocusHandler)
                }
            }
        }

        const autoToggleFocusHandler = () => focusToggler(true);
        const manualToggleFocusHandler = () => focusToggler(false);

        this.mainHtml.addEventListener('mouseleave', unfocusHandler)

        if (this.isDesktop) {
            this.fullscreen.subscribe((fullscreen) => {
                if (fullscreen) {
                    this.mainHtml.removeEventListener('mouseover', manualToggleFocusHandler);
                    this.mainHtml.addEventListener('mousemove', autoToggleFocusHandler);
                    handleIdleTimeout(unfocusHandler);
                } else {
                    this.mainHtml.addEventListener('mouseover', manualToggleFocusHandler);
                    this.mainHtml.removeEventListener('mousemove', autoToggleFocusHandler);
                    handleIdleTimeout(null);
                }
            })

            this.mainHtml.addEventListener('mouseover', manualToggleFocusHandler);

        } else {
            this.mainHtml.addEventListener('click', (evt) => {
                evt.preventDefault();
                evt.stopPropagation();
                evt.stopImmediatePropagation();
                autoToggleFocusHandler();
            })
        }

        this.controlBar?.onChangeThumbPreview((evt) => {
            if (evt) {
                const scaleX = this.previewThumb.clientWidth / evt.frame.w;
                const scaleY = this.previewThumb.clientHeight / evt.frame.h;
                this.previewThumb.style.background = `url("${evt.bgImg}") ${evt.frame.bgX * scaleX}px ${evt.frame.bgY * scaleY}px / ${evt.frame.bgW * scaleX}px ${evt.frame.bgH * scaleY}px`;
                this.previewThumb.style.visibility = "visible";
            } else {
                this.previewThumb.style.visibility = "hidden";
            }
        })
    }

}
